import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import DynamicFavicon from "./components/seo/DynamicFavicon";
import ErrorBoundary from "./components/ErrorBoundary";
import PageLoader from "./components/ui/page-loader";
import PrefetchProvider from "./components/PrefetchProvider";
// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const CoveragePage = lazy(() => import("./pages/CoveragePage"));
const OffersPage = lazy(() => import("./pages/OffersPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const TagsPage = lazy(() => import("./pages/TagsPage"));
const TagPage = lazy(() => import("./pages/TagPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));

// Admin pages - lazy loaded
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminOffers = lazy(() => import("./pages/admin/AdminOffers"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminCoverageAreas = lazy(() => import("./pages/admin/AdminCoverageAreas"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminStatistics = lazy(() => import("./pages/admin/AdminStatistics"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminSEO = lazy(() => import("./pages/admin/AdminSEO"));
const AdminSocialMedia = lazy(() => import("./pages/admin/AdminSocialMedia"));
const AdminRedirects = lazy(() => import("./pages/admin/AdminRedirects"));
const AdminLegalPages = lazy(() => import("./pages/admin/AdminLegalPages"));
const AdminSEOAudit = lazy(() => import("./pages/admin/AdminSEOAudit"));
const AdminBackup = lazy(() => import("./pages/admin/AdminBackup"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminActivityLog = lazy(() => import("./pages/admin/AdminActivityLog"));
const AdminTags = lazy(() => import("./pages/admin/AdminTags"));
const AdminHomepage = lazy(() => import("./pages/admin/AdminHomepage"));
const AdminKeywords = lazy(() => import("./pages/admin/AdminKeywords"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminSearchAnalytics = lazy(() => import("./pages/admin/AdminSearchAnalytics"));
const AdminSitemap = lazy(() => import("./pages/admin/AdminSitemap"));
const AdminIndexing = lazy(() => import("./pages/admin/AdminIndexing"));
const AdminImageAudit = lazy(() => import("./pages/admin/AdminImageAudit"));
const AdminMigration = lazy(() => import("./pages/admin/AdminMigration"));

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <PrefetchProvider />
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <DynamicFavicon />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:slug" element={<ServicePage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<ArticlePage />} />
              <Route path="/coverage" element={<CoveragePage />} />
              <Route path="/offers" element={<OffersPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/tags" element={<TagsPage />} />
              <Route path="/tags/:slug" element={<TagPage />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/auth" element={<AuthPage />} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
              <Route path="/admin/services" element={<AdminLayout><AdminServices /></AdminLayout>} />
              <Route path="/admin/articles" element={<AdminLayout><AdminArticles /></AdminLayout>} />
              <Route path="/admin/messages" element={<AdminLayout><AdminMessages /></AdminLayout>} />
              <Route path="/admin/offers" element={<AdminLayout><AdminOffers /></AdminLayout>} />
              <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
              <Route path="/admin/coverage" element={<AdminLayout><AdminCoverageAreas /></AdminLayout>} />
              <Route path="/admin/testimonials" element={<AdminLayout><AdminTestimonials /></AdminLayout>} />
              <Route path="/admin/analytics" element={<AdminLayout><AdminStatistics /></AdminLayout>} />
              <Route path="/admin/media" element={<AdminLayout><AdminMedia /></AdminLayout>} />
              <Route path="/admin/seo" element={<AdminLayout><AdminSEO /></AdminLayout>} />
              <Route path="/admin/social" element={<AdminLayout><AdminSocialMedia /></AdminLayout>} />
              <Route path="/admin/redirects" element={<AdminLayout><AdminRedirects /></AdminLayout>} />
              <Route path="/admin/legal" element={<AdminLayout><AdminLegalPages /></AdminLayout>} />
              <Route path="/admin/seo-audit" element={<AdminLayout><AdminSEOAudit /></AdminLayout>} />
              <Route path="/admin/backup" element={<AdminLayout><AdminBackup /></AdminLayout>} />
              <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
              <Route path="/admin/activity" element={<AdminLayout><AdminActivityLog /></AdminLayout>} />
              <Route path="/admin/tags" element={<AdminLayout><AdminTags /></AdminLayout>} />
              <Route path="/admin/homepage" element={<AdminLayout><AdminHomepage /></AdminLayout>} />
              <Route path="/admin/keywords" element={<AdminLayout><AdminKeywords /></AdminLayout>} />
              <Route path="/admin/bookings" element={<AdminLayout><AdminBookings /></AdminLayout>} />
              <Route path="/admin/search-analytics" element={<AdminLayout><AdminSearchAnalytics /></AdminLayout>} />
              <Route path="/admin/sitemap" element={<AdminLayout><AdminSitemap /></AdminLayout>} />
              <Route path="/admin/indexing" element={<AdminLayout><AdminIndexing /></AdminLayout>} />
              <Route path="/admin/image-audit" element={<AdminLayout><AdminImageAudit /></AdminLayout>} />
              <Route path="/admin/migration" element={<AdminLayout><AdminMigration /></AdminLayout>} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
